// Sealy site footer rendered in-tree by PageSkeleton (#33285 P3/P5). The footer
// lives INSIDE <main class="main-content"> (unlike cocoon). It composes two
// existing islands -- FooterWidgets (the talk-to-expert / nearby-stores widget
// row, rendered ABOVE <footer>) and the conditional ServiceFooter carousel --
// plus the static social/copyright/legal markup.
//
// The footer navigation is serialized from the "footer" NavMenu in
// chrome._build_footer_nav_columns (mirroring how the header serializes the top
// NavMenu) and rendered here as a real React tree: the single
// <nav class="footer__nav"> whose columns and lists carry each
// category's heading plus its ordered sub_nav items (links interleaved with
// raw-HTML blocks, each inside the w-block-* wrapper render_basic emitted).
import { Immutable } from "immer";
import React from "react";
import { FooterWidgetsIsland as FooterWidgets } from "tsi-common-react/src/components/FooterWidgetsIsland/index";
import { ServiceFooterIsland as ServiceFooter } from "tsi-common-react/src/components/ServiceFooterIsland/index";
import { t } from "ttag";

import { Context } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type ISocial = Immutable<{
    facebook: string;
    twitter: string;
    youtube: string;
    instagram: string;
    pinterest: string;
}>;

// Derived from the reactivated Context so the shapes stay in lockstep with the
// GlobalChrome NamedTuples (FooterNavColumn / FooterNavCategory / FooterNavItem).
type IFooterNavColumns = React.ContextType<
    typeof Context
>["chrome"]["footer_nav_columns"];
type IFooterNavItem =
    IFooterNavColumns[number]["categories"][number]["items"][number];

type IProps = Immutable<{
    social: ISocial;
    copyrightYear: string;
    footerCopyHtml: string;
    footerNavColumns: IFooterNavColumns;
    privacyPolicyUrl: string;
    termsUrl: string;
    staticBase: string;
    footerPhone: string;
    footerShowContactSupport: boolean;
    footerShowNearbyStores: boolean;
    footerChatOfflineLink: string | null;
    showServiceFooter: boolean;
    serviceFooterSnippetsJson: string;
    serviceFooterInterval: number | null;
    serviceFooterLoops: number | null;
}>;

// A single footer list sub_nav item. Every item carries the
// `w-block-{type} block-{type}` wrapper that Wagtail's `render_basic` emitted
// around each `{{ self.sub_nav }}` child. A raw block injects its server render
// (e.g. an html block) directly into that wrapper; a link block renders the
// `<li><a>` structure the deleted `menu_link.html` produced.
const FooterNavItem = (
    props: Immutable<{ item: IFooterNavItem }>,
): React.JSX.Element => {
    const { item } = props;
    const wblockClass = `w-block-${item.block_type} block-${item.block_type}`;
    if (item.is_raw) {
        return (
            <EditorContent>
                {(marker) => (
                    <div
                        className={wblockClass}
                        dangerouslySetInnerHTML={{ __html: item.raw_html }}
                        {...marker}
                    />
                )}
            </EditorContent>
        );
    }
    return (
        <div className={wblockClass}>
            <li>
                <a
                    href={item.url}
                    target={item.open_in_new_tab ? "_blank" : undefined}
                    rel={
                        item.open_in_new_tab ? "noopener noreferrer" : undefined
                    }
                >
                    {item.title}
                </a>
            </li>
        </div>
    );
};

export const SiteFooter = (props: IProps): React.JSX.Element => {
    const { social, staticBase } = props;
    return (
        <>
            <FooterWidgets
                phone_number={props.footerPhone}
                show_contact_support={props.footerShowContactSupport}
                show_nearby_stores={props.footerShowNearbyStores}
                chat_offline_link={props.footerChatOfflineLink}
            />
            <footer className={styles.footer}>
                <div
                    className={concatClassNames([
                        styles.footerContainer,
                        "container",
                    ])}
                >
                    {/* The "footer" NavMenu rendered as a real React tree from
                     * the typed columns serialized in
                     * chrome._build_footer_nav_columns. It sits directly in
                     * .container's flow (sealy's .footer__nav floats on mobile
                     * and .container lays its children out directly), so each
                     * <a> carries its own target and nothing mutates it after
                     * hydration. */}
                    <nav className="footer__nav">
                        {props.footerNavColumns.map((column, columnIndex) => (
                            <div key={columnIndex} className={styles.footerCol}>
                                <ul className={styles.footerList}>
                                    {column.categories.map(
                                        (category, categoryIndex) => (
                                            <React.Fragment key={categoryIndex}>
                                                <li
                                                    className={
                                                        styles.footerCategoryHeading
                                                    }
                                                    role="heading"
                                                    aria-level={2}
                                                >
                                                    {category.title}
                                                </li>
                                                {category.items.map(
                                                    (item, itemIndex) => (
                                                        <FooterNavItem
                                                            key={itemIndex}
                                                            item={item}
                                                        />
                                                    ),
                                                )}
                                            </React.Fragment>
                                        ),
                                    )}
                                </ul>
                            </div>
                        ))}
                    </nav>
                    <div className={styles.footerBadge}>
                        <a
                            href="https://allyant.com"
                            title={t`Allyant ADA conformance`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                className="img-responsive"
                                src={`${staticBase}img/logos/badge-A360.png`}
                                alt={t`Allyant ADA conformance certified`}
                                width={90}
                                height={90}
                                loading="lazy"
                                decoding="async"
                            />
                        </a>
                    </div>
                    <div className={styles.footerSignup}>
                        <nav className={styles.footerSocial}>
                            <a
                                className={styles.footerSocialItem}
                                href={social.facebook}
                                title={t`Sealy on Facebook`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    className={concatClassNames([
                                        styles.footerSocialItemFb,
                                        "img-responsive",
                                    ])}
                                    src={`${staticBase}img/social/icon-facebook.svg`}
                                    alt={t`Facebook icon`}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </a>
                            <a
                                className={styles.footerSocialItem}
                                href={social.twitter}
                                title={t`Sealy on Twitter`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    className={concatClassNames([
                                        styles.footerSocialItemTwitter,
                                        "img-responsive",
                                    ])}
                                    src={`${staticBase}img/social/icon-x.svg`}
                                    alt={t`Twitter icon`}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </a>
                            <a
                                className={styles.footerSocialItem}
                                href={social.youtube}
                                title={t`Sealy on YouTube`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    className={concatClassNames([
                                        styles.footerSocialItemYt,
                                        "img-responsive",
                                    ])}
                                    src={`${staticBase}img/social/icon-yt.svg`}
                                    alt={t`YouTube icon`}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </a>
                            <a
                                className={styles.footerSocialItem}
                                href={social.instagram}
                                title={t`Sealy on Instagram`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    className={concatClassNames([
                                        styles.footerSocialItemIg,
                                        "img-responsive",
                                    ])}
                                    src={`${staticBase}img/social/icon-ig.svg`}
                                    alt={t`Instagram icon`}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </a>
                            <a
                                className={styles.footerSocialItem}
                                href={social.pinterest}
                                title={t`Sealy on Pinterest`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    className={concatClassNames([
                                        styles.footerSocialItemPt,
                                        "img-responsive",
                                    ])}
                                    src={`${staticBase}img/social/icon-pt.svg`}
                                    alt={t`Pinterest icon`}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </a>
                        </nav>
                    </div>
                    <div className={styles.footerCopyrightLegal}>
                        {props.footerCopyHtml && (
                            // richtext output is wrapped in a
                            // <div class="rich-text">.
                            <div
                                className="rich-text"
                                dangerouslySetInnerHTML={{
                                    __html: props.footerCopyHtml,
                                }}
                            />
                        )}
                        <div className={styles.footerCopyright}>
                            &copy; {props.copyrightYear} Sealy Technology LLC
                        </div>
                        <ul className={styles.footerLegalLinks}>
                            <li>
                                <a
                                    href={props.privacyPolicyUrl}
                                    title={t`Privacy Policy`}
                                >
                                    {t`Privacy Policy`}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={props.termsUrl}
                                    title={t`Terms and Conditions`}
                                >
                                    {t`Terms & Conditions`}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
                {props.showServiceFooter && (
                    <ServiceFooter
                        snippets={props.serviceFooterSnippetsJson}
                        interval={props.serviceFooterInterval}
                        loops={props.serviceFooterLoops}
                    />
                )}
            </footer>
        </>
    );
};
